import { Link } from "@tanstack/react-router";
import {
  BadgeCheck,
  Bell,
  CreditCard,
  LifeBuoy,
  ListChecks,
  Lock,
  MessageSquare,
  Receipt,
  Settings,
  Share2,
  ShieldCheck,
  UserRound,
  Wallet,
} from "lucide-react";
import { ContactForm } from "@/components/contact-form";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useT } from "@/i18n";

const FAQ_EN: [string, string][] = [
  [
    "Where do I find my dashboard?",
    "Sign in and open the account menu in the header, then Dashboard. The overview shows your balances, listings, public page link and pending actions.",
  ],
  [
    "How do I change my public page address?",
    "Profile → slug. It updates behot.now/user/your-slug immediately, and your referral link follows the new address.",
  ],
  [
    "How do I share my page with the referral code?",
    "Copy the link from the dashboard overview or the Referrals page — it already contains your ?ref= code.",
  ],
  [
    "What is the difference between the three wallets?",
    "Top-up Heat pays for listings, boosts, featuring and Marketplace orders. Earn holds creator revenue and is withdrawable. Reward Heat comes from referrals and promos and is spent first as internal credit.",
  ],
  [
    "How do I add or change my payout method?",
    "Profile → payout methods. Adding or editing a bank account or crypto wallet requires an OTP sent to your registered email.",
  ],
  [
    "Where are my invoices?",
    "Invoices in the dashboard. Every top-up generates a sequential PDF invoice, also emailed to you automatically.",
  ],
  [
    "How do I switch between user and professional dashboard?",
    "Use the account mode switch in the dashboard. Professional mode adds selling, service offers, verification and payouts; you can switch back at any time.",
  ],
  [
    "Why is my listing pending?",
    "New and edited listings go to moderation. If no admin acts within 20 minutes, the system approves it automatically.",
  ],
  [
    "Can I edit a listing after it is live?",
    "Yes. Editing sends it back to moderation, and the listing keeps its Heat and position while it is reviewed.",
  ],
  [
    "How do I control notifications and emails?",
    "Notification preferences and the marketing opt-in live in Profile. Security, invoice and order emails cannot be disabled.",
  ],
  [
    "How do I export my history?",
    "Transactions has a PDF export with every credit and debit, useful for accounting or a tax return.",
  ],
  [
    "How do I close my account?",
    "Contact support from the form below. Remaining Earn balance must be withdrawn first; Heat credit is not refundable.",
  ],
];

const STEP_KEYS = ["step1", "step2", "step3", "step4", "step5"] as const;
const AREA_KEYS = [
  { key: "profile", icon: UserRound, to: "/app/profile" as const },
  { key: "wallets", icon: Wallet, to: "/app/wallets" as const },
  { key: "transactions", icon: Receipt, to: "/app/transactions" as const },
  { key: "billing", icon: CreditCard, to: "/app/billing" as const },
  { key: "notifications", icon: Bell, to: "/app/notifications" as const },
  { key: "messages", icon: MessageSquare, to: "/app/messages" as const },
];
const CONFIG_KEYS = ["config1", "config2", "config3", "config4", "config5", "config6"] as const;
const TRUST_KEYS = [
  { key: "trust1", icon: ShieldCheck },
  { key: "trust2", icon: Lock },
  { key: "trust3", icon: BadgeCheck },
];
const FAQ_KEYS = FAQ_EN.map((_, i) => `faq${i + 1}`);

export function ProfileHowItWorks() {
  const { t } = useT();

  const STEPS = STEP_KEYS.map((k) => ({
    key: k,
    title: t(`hiw.profile.${k}.title`),
    body: t(`hiw.profile.${k}.body`),
  }));
  const AREAS = AREA_KEYS.map((a) => ({
    ...a,
    title: t(`hiw.profile.area.${a.key}.title`),
    body: t(`hiw.profile.area.${a.key}.body`),
  }));
  const CONFIG = CONFIG_KEYS.map((k) => t(`hiw.profile.${k}`));
  const TRUST = TRUST_KEYS.map((p) => ({
    ...p,
    title: t(`hiw.profile.${p.key}.title`),
    body: t(`hiw.profile.${p.key}.body`),
  }));
  const FAQ: [string, string][] = FAQ_KEYS.map((k) => [t(`hiw.profile.${k}.q`), t(`hiw.profile.${k}.a`)]);

  return (
    <div>
      <p className="mt-8 max-w-2xl text-muted-foreground">{t("hiw.profile.intro")}</p>

      <section className="mt-12" aria-labelledby="prof-steps-heading">
        <div className="flex items-center gap-2">
          <Settings className="size-5 text-primary" aria-hidden="true" />
          <h2 id="prof-steps-heading" className="font-display text-2xl font-bold">
            {t("hiw.profile.stepsHeading")}
          </h2>
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <ol className="contents list-none">
            {STEPS.map((s) => (
              <li key={s.key} className="card-surface p-5">
                <h3 className="font-display text-lg font-bold">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
              </li>
            ))}
          </ol>
          <div className="card-surface flex flex-col justify-center p-5">
            <h3 className="font-display text-lg font-bold">{t("hiw.profile.dashboardTitle")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("hiw.profile.dashboardBody")}</p>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <Button asChild variant="hot" className="w-full sm:w-auto">
                <Link to="/app">{t("hiw.profile.goDashboard")}</Link>
              </Button>
              <Button asChild variant="outline" className="w-full sm:w-auto">
                <Link to="/app/profile">{t("hiw.profile.editProfile")}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-14" aria-labelledby="prof-areas-heading">
        <div className="flex items-center gap-2">
          <Share2 className="size-5 text-primary" aria-hidden="true" />
          <h2 id="prof-areas-heading" className="font-display text-2xl font-bold">
            {t("hiw.profile.areasHeading")}
          </h2>
        </div>
        <ul className="mt-5 grid list-none gap-4 sm:grid-cols-2">
          {AREAS.map((a) => (
            <li key={a.key} className="card-surface p-5">
              <a.icon className="size-5 text-primary" aria-hidden="true" />
              <h3 className="mt-3 font-display text-lg font-bold">{a.title}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{a.body}</p>
              <Link
                to={a.to}
                className="mt-3 inline-flex text-sm font-semibold text-primary hover:underline"
              >
                {t("hiw.profile.openLabel")} {a.title}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-14" aria-labelledby="prof-config-heading">
        <h2 id="prof-config-heading" className="font-display text-2xl font-bold">
          {t("hiw.profile.configHeading")}
        </h2>
        <div className="card-surface mt-5 p-6">
          <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
            {CONFIG.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mt-14" aria-labelledby="prof-trust-heading">
        <h2 id="prof-trust-heading" className="font-display text-2xl font-bold">
          {t("hiw.profile.trustHeading")}
        </h2>
        <ul className="mt-5 grid list-none gap-4 sm:grid-cols-3">
          {TRUST.map((p) => (
            <li key={p.key} className="card-surface p-5">
              <p.icon className="size-5 text-primary" aria-hidden="true" />
              <h3 className="mt-3 font-display text-lg font-bold">{p.title}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{p.body}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-14" aria-labelledby="prof-faq-heading">
        <div className="flex flex-wrap items-center gap-2">
          <ListChecks className="size-5 text-primary" aria-hidden="true" />
          <h2 id="prof-faq-heading" className="font-display text-2xl font-bold">
            {t("hiw.profile.faqHeading")}
          </h2>
          <Badge variant="outline">{t("hiw.profile.faqCount", { count: FAQ.length })}</Badge>
        </div>
        <div className="mt-5 divide-y divide-border rounded-xl border border-border">
          {FAQ.map(([q, a], i) => (
            <details
              key={q}
              open={i < 3}
              className="group px-5 py-4 [&_summary::-webkit-details-marker]:hidden"
            >
              <summary className="cursor-pointer list-none font-display font-bold">{q}</summary>
              <p className="mt-2 text-sm text-muted-foreground">{a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="mt-14" aria-labelledby="prof-contact-heading">
        <div className="flex items-center gap-2">
          <LifeBuoy className="size-5 text-primary" aria-hidden="true" />
          <h2 id="prof-contact-heading" className="font-display text-2xl font-bold">
            {t("hiw.profile.contactHeading")}
          </h2>
        </div>
        <div className="mt-5">
          <ContactForm defaultTopic="Request support" />
        </div>
      </section>
    </div>
  );
}

export const PROFILE_FAQ = FAQ_EN;
